import { useEffect, useMemo, useState, type ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSparkles } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import clsx from "clsx";
import {
  listOpenworkDemoLetters,
  type FaClientListItem,
  type OpenworkDemoLetterCardDto,
} from "@/services/api/agentic-v2";
import type { TaskSurfaceCard } from "@/services/api/agentic-v2/types";
import { Button } from "@/components/ui/button/Button";
import { ClientAvatar } from "@/components/clients/ClientAvatar";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { HOME_SURFACE_RAISED } from "@/components/home/homeSurfaces";
import { useHomeSurfaceData } from "@/components/home/useHomeSurfaceData";
import { OPENWORK_ADVISOR_SURFACE_ID } from "@/components/home/resolveHomeSurfaceId";
import {
  reviewQueueActions,
  reviewQueueClientName,
  reviewQueueFaClientId,
  reviewQueueNote,
  reviewQueueTitle,
} from "@/components/agentic-surface/widgets/home/reviewQueueModel";
import { useAuth } from "@/context/AuthContext";
import { openworkClientSnapshot } from "@/components/clients/openworkClientSnapshots";
import { normalizeOpenworkLetterReviewQueue } from "@/components/review/reviewDemoHelpers";
import { OPENWORK_HOME_REFRESH_EVENT } from "@/components/review/openworkDemoState";
import { useOpenTaskReview } from "@/components/review/useOpenTaskReview";
import type { ReviewOpenFocus } from "@/components/review/reviewOpenMode";

type OpenworkClientsViewProps = {
  clients: FaClientListItem[];
};

export function OpenworkClientsView({ clients }: OpenworkClientsViewProps) {
  const { user } = useAuth();
  const openTaskReview = useOpenTaskReview();
  const [letterCards, setLetterCards] = useState<OpenworkDemoLetterCardDto[]>([]);
  const { data: surfaceData } = useHomeSurfaceData({
    surfaceId: OPENWORK_ADVISOR_SURFACE_ID,
    visibility: "me",
  });

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const dto = await listOpenworkDemoLetters();
        if (!cancelled) setLetterCards(dto.items ?? []);
      } catch {
        if (!cancelled) setLetterCards([]);
      }
    };
    void refresh();
    const handler = () => {
      void refresh();
    };
    window.addEventListener(OPENWORK_HOME_REFRESH_EVENT, handler);
    return () => {
      cancelled = true;
      window.removeEventListener(OPENWORK_HOME_REFRESH_EVENT, handler);
    };
  }, []);

  const reviewTasks = useMemo(
    () =>
      normalizeOpenworkLetterReviewQueue(surfaceData?.needsMeTasks ?? [], {
        assigneeEmail: user?.email,
      }),
    [surfaceData?.needsMeTasks, user?.email],
  );

  const sortedClients = useMemo(() => {
    const deduped = dedupeOpenworkClients(clients, reviewTasks);
    return deduped.sort((left, right) => {
      const leftTask = findClientReviewTask(left, reviewTasks);
      const rightTask = findClientReviewTask(right, reviewTasks);
      if (leftTask && !rightTask) return -1;
      if (!leftTask && rightTask) return 1;
      if (left.open_task_count !== right.open_task_count) {
        return right.open_task_count - left.open_task_count;
      }
      return left.display_name.localeCompare(right.display_name);
    });
  }, [clients, reviewTasks]);

  const handleOpenTask = (task: TaskSurfaceCard, focus?: ReviewOpenFocus) => {
    openTaskReview(task._id, reviewQueueTitle(task), { focus });
  };

  const handleOpenLetterCard = (card: OpenworkDemoLetterCardDto, focus: ReviewOpenFocus) => {
    openTaskReview(card.task_id, `Review ${card.client_name}`, { focus });
  };

  return (
    <div
      className="grid items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-3"
      data-testid="openwork-clients-grid"
    >
      {sortedClients.map((client) => (
        <OpenworkClientCard
          key={client._id}
          client={client}
          reviewTask={findClientReviewTask(client, reviewTasks)}
          letterCard={findClientLetterCard(client, letterCards)}
          onOpenTask={handleOpenTask}
          onOpenLetterCard={handleOpenLetterCard}
        />
      ))}
    </div>
  );
}

type OpenworkClientCardProps = {
  client: FaClientListItem;
  reviewTask?: TaskSurfaceCard;
  letterCard?: OpenworkDemoLetterCardDto;
  onOpenTask: (task: TaskSurfaceCard, focus?: ReviewOpenFocus) => void;
  onOpenLetterCard: (card: OpenworkDemoLetterCardDto, focus: ReviewOpenFocus) => void;
};

function OpenworkClientCard({
  client,
  reviewTask,
  letterCard,
  onOpenTask,
  onOpenLetterCard,
}: OpenworkClientCardProps) {
  const actions = reviewTask ? reviewQueueActions(reviewTask) : [];
  const caseClosed =
    letterCard?.status === "done" || letterCard?.status === "auto_closed";
  const aniTake =
    (reviewTask ? reviewQueueNote(reviewTask) : null) ||
    client.ai_insight?.summary?.trim() ||
    "I'm monitoring this client for anything that needs your attention.";

  return (
    <article
      className={clsx(
        HOME_SURFACE_RAISED,
        "flex h-full min-h-[16rem] flex-col p-4 text-left",
        reviewTask && "ring-1 ring-amber-400/25",
      )}
      data-testid={`openwork-client-card-${client._id}`}
    >
      <div className="flex items-start gap-3">
        <ClientAvatar name={client.display_name} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate text-sm font-semibold text-slate-100">
              {client.display_name}
            </h3>
            <span className="shrink-0 text-[11px] text-slate-500 tabular-nums">
              {client.last_activity_at
                ? formatRelativeTime(client.last_activity_at)
                : "—"}
            </span>
          </div>
          <p className="mt-1 line-clamp-2 min-h-10 text-xs leading-relaxed text-slate-400">
            {reviewTask
              ? reviewQueueTitle(reviewTask)
              : caseClosed
                ? letterCard?.user_ask ?? letterCard?.kind_label ?? "Case closed"
                : ""}
          </p>
        </div>
      </div>

      <div className="mt-4 flex-1 space-y-3">
        <CardSection title="Snapshot">
          <p className="line-clamp-2 min-h-10 text-xs leading-relaxed text-slate-300">
            {openworkClientSnapshot(client.display_name, reviewTask)}
          </p>
        </CardSection>

        <CardSection title="Ani's take">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-slate-300">
            <FontAwesomeIcon
              icon={faSparkles}
              className="mt-0.5 shrink-0 text-indigo-300/80"
              aria-hidden
            />
            <span className="line-clamp-3 min-h-[3.75rem]">{aniTake}</span>
          </p>
        </CardSection>
      </div>

      <div className="mt-4">
        {reviewTask ? (
          <div className="flex flex-wrap gap-2">
            {actions.map((action) => {
              const focus: ReviewOpenFocus =
                action.kind === "open_letter" ? "letter" : "case";
              return (
                <Button
                  key={action.id}
                  color={action.kind === "open_case" ? "indigo" : undefined}
                  outline={action.kind === "open_letter"}
                  size="sm"
                  onClick={() => onOpenTask(reviewTask, focus)}
                >
                  {action.label}
                </Button>
              );
            })}
          </div>
        ) : letterCard && caseClosed ? (
          <div className="flex flex-wrap gap-2">
            <Button
              outline
              size="sm"
              onClick={() => onOpenLetterCard(letterCard, "case")}
            >
              View audit log
            </Button>
            <Button plain size="sm" onClick={() => onOpenLetterCard(letterCard, "letter")}>
              View letter
            </Button>
          </div>
        ) : (
          <p className="text-xs text-slate-500">No tasks outstanding.</p>
        )}
      </div>
    </article>
  );
}

function CardSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
        {title}
      </p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function dedupeOpenworkClients(
  clients: FaClientListItem[],
  reviewTasks: TaskSurfaceCard[],
): FaClientListItem[] {
  const byName = new Map<string, FaClientListItem>();
  for (const client of clients) {
    const key = client.display_name.trim().toLowerCase();
    const existing = byName.get(key);
    if (!existing) {
      byName.set(key, client);
      continue;
    }
    const existingHasTask = Boolean(findClientReviewTask(existing, reviewTasks));
    const clientHasTask = Boolean(findClientReviewTask(client, reviewTasks));
    if (clientHasTask && !existingHasTask) {
      byName.set(key, client);
      continue;
    }
    if (client.open_task_count > existing.open_task_count) {
      byName.set(key, client);
    }
  }
  return [...byName.values()];
}

function findClientReviewTask(
  client: FaClientListItem,
  tasks: TaskSurfaceCard[],
): TaskSurfaceCard | undefined {
  const byId = tasks.find((task) => reviewQueueFaClientId(task) === client._id);
  if (byId) return byId;

  const normalizedName = client.display_name.trim().toLowerCase();
  return tasks.find(
    (task) => reviewQueueClientName(task)?.trim().toLowerCase() === normalizedName,
  );
}

function findClientLetterCard(
  client: FaClientListItem,
  cards: OpenworkDemoLetterCardDto[],
): OpenworkDemoLetterCardDto | undefined {
  const normalizedName = client.display_name.trim().toLowerCase();
  return cards.find((card) => card.client_name.trim().toLowerCase() === normalizedName);
}
